import { useEffect, useState } from 'react'
import { api } from '../../api/client'

interface OrderItem {
  name: string
  qty: number
  price: number
}

interface Order {
  id: string
  buyer_name: string
  buyer_phone?: string
  buyer_address?: string
  subtotal: number
  created_at: string
  items: OrderItem[]
  notes?: string
}

interface OrdersResponse {
  orders: Order[]
  total?: number
}

const PAGE_SIZE = 20

function orderAge(createdAt: string): 'today' | 'week' | 'older' {
  const d = new Date(createdAt)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return 'today'
  if (now.getTime() - d.getTime() < 7 * 24 * 60 * 60 * 1000) return 'week'
  return 'older'
}

export function StoreOrders() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  async function fetchOrders(currentOffset: number, append = false) {
    try {
      const data = await api.get<OrdersResponse | Order[]>(
        `/api/store/orders?limit=${PAGE_SIZE}&offset=${currentOffset}`
      )
      const list = Array.isArray(data) ? data : (data.orders ?? [])
      setOrders(prev => append ? [...prev, ...list] : list)
      setHasMore(list.length === PAGE_SIZE)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al cargar pedidos')
    }
  }

  useEffect(() => {
    fetchOrders(0).finally(() => setLoading(false))
  }, [])

  async function loadMore() {
    const newOffset = offset + PAGE_SIZE
    setLoadingMore(true)
    await fetchOrders(newOffset, true)
    setOffset(newOffset)
    setLoadingMore(false)
  }

  if (loading) return (
    <div>
      <div className="admin-header">
        <div><h1 className="admin-title">Pedidos</h1></div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 100, borderRadius: 16 }} />)}
      </div>
    </div>
  )

  return (
    <div className="animate-in">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Pedidos</h1>
          <p className="admin-sub">Más recientes primero · {orders.length} cargado{orders.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {orders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <p>Aún no hay pedidos</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {orders.map(order => {
            const age = orderAge(order.created_at)
            const expanded = expandedId === order.id
            const waHref = order.buyer_phone
              ? `https://wa.me/${order.buyer_phone.replace(/\D/g, '')}`
              : null

            return (
              <div key={order.id} className="order-card animate-in">
                <div className={`order-card-header-bar ${age}`}>
                  <div>
                    <strong style={{ fontSize: '1rem' }}>{order.buyer_name}</strong>
                    {age === 'today' && (
                      <span className="badge badge-brand" style={{ marginLeft: 8, fontSize: '.65rem' }}>HOY</span>
                    )}
                    {waHref && (
                      <a href={waHref} target="_blank" rel="noopener noreferrer" className="order-card-phone">
                        📱 {order.buyer_phone}
                      </a>
                    )}
                    {order.buyer_address && (
                      <span className="order-card-address">📍 {order.buyer_address}</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>Q {Number(order.subtotal).toFixed(2)}</div>
                    <div className="order-card-date">
                      {new Date(order.created_at).toLocaleDateString('es-GT', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>

                <div className="order-card-footer">
                  <span className="order-card-items">
                    {order.items?.length ?? 0} producto{order.items?.length !== 1 ? 's' : ''}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {waHref && (
                      <a
                        href={waHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm"
                        style={{ background: '#25D366', color: '#fff', border: 'none', textDecoration: 'none' }}
                      >
                        WhatsApp
                      </a>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setExpandedId(expanded ? null : order.id)}
                    >
                      {expanded ? '▲ Ocultar' : '▼ Ver productos'}
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="order-items-expanded">
                    {order.items?.map((item, i) => (
                      <div key={i} className="order-item-row">
                        <span>{item.qty}x {item.name}</span>
                        <span style={{ fontWeight: 600 }}>Q {(item.price * item.qty).toFixed(2)}</span>
                      </div>
                    ))}
                    {order.notes && (
                      <p className="order-notes">📝 {order.notes}</p>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {hasMore && (
            <button
              type="button"
              className="btn btn-outline btn-full"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Cargando...' : 'Cargar más pedidos'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
